/**
 * CreatorsPage - live data only. /api/v1/dashboard/creators.
 *
 * Hero tiles split into:
 *   • Total creators (lifetime)
 *   • New today (literal today, never windowed)
 *   • Active in window — creators with COMPLETED calls in selected range
 *   • Verified KYC (lifetime — is_verify flag)
 *   • At-risk — real count from a dedicated COUNT query (was broken: always 0)
 *
 * Top creators leaderboard sourced from `earnings` table (real paid earnings),
 * not `celebrity_slot_booking.price` (gross of missed/failed bookings).
 */
import React from "react";
import { tokensV2 as T } from "./tokens.v2";
import { Section } from "./Section.v2";
import { PageGuide } from "./PageGuide.v2";
import { Tile } from "./Tile.v2";
import { AreaChart } from "./AreaChart.v2";
import { Leaderboard } from "./Leaderboard.v2";
import { useDashboardPage, formatINR, formatNum } from "./useDashboardPage.v2";
import { CreatorEarningsTables } from "./CreatorEarningsTables.v2";
import { rangeWordFor, granLabel as glab, granWord as gword } from "./rangeWord.v2";
import { LoadingShell, EmptyError } from "./PageState.v2";

function ErrorBanner({ message, onRetry }) {
  return (
    <div style={{ marginBottom: 12, padding: "10px 14px", background: T.status.warnBg, border: `1px solid ${T.status.warn}`, borderRadius: 8, fontSize: 12, color: T.text.secondary, display: "flex", alignItems: "center", gap: 10 }}>
      <strong>Backend error:</strong> {message}.
      <button type="button" onClick={onRetry} style={{ background: T.surface.card, border: `1px solid ${T.border.subtle}`, borderRadius: 8, padding: "5px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Retry</button>
    </div>
  );
}

/* ─── App Version widget — grouped by platform → version → list of creators ─── */

// Semver-aware sort: returns positive if a > b
function compareVersions(a, b) {
  const parse = v => (v || "").replace(/[^0-9.]/g, "").split(".").map(n => parseInt(n, 10) || 0);
  const pa = parse(a), pb = parse(b);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const da = pa[i] || 0, db = pb[i] || 0;
    if (da !== db) return da - db;
  }
  return 0;
}

function AppVersionsTable({ rows = [] }) {
  // Group by platform → version → creators. Memoised so a state change in
  // selectedKey doesn't reshape the data on every render.
  const { grouped, allVersions } = React.useMemo(() => {
    const g = {}; // { android: { '5.2.1': [creators...] }, ios: {...} }
    for (const r of rows) {
      const platform = r.deviceType || "unknown";
      const version  = r.appVersion || "unknown";
      g[platform] = g[platform] || {};
      g[platform][version] = g[platform][version] || [];
      g[platform][version].push(r);
    }
    const list = [];
    Object.keys(g).sort().forEach(p => {
      const versions = Object.keys(g[p]).sort(compareVersions).reverse();
      versions.forEach(v => {
        list.push({
          platform: p,
          version: v,
          creators: g[p][v],
          isLatest: v === versions[0],
          count: g[p][v].length,
        });
      });
    });
    return { grouped: g, allVersions: list };
  }, [rows]);

  // Default selection: pick the largest cohort overall (most actionable signal).
  // Hook MUST run before any conditional return — Rules of Hooks. Empty-data
  // case is handled below the hook.
  const defaultKey = React.useMemo(() => {
    if (!allVersions.length) return "";
    const best = allVersions.reduce((b, v) => v.count > b.count ? v : b, allVersions[0]);
    return `${best.platform}::${best.version}`;
  }, [allVersions]);
  const [selectedKey, setSelectedKey] = React.useState(defaultKey);

  // If the data shape changes (different rows arrive) and the previously
  // selected key no longer exists, snap to the new default.
  React.useEffect(() => {
    if (defaultKey && !allVersions.some(v => `${v.platform}::${v.version}` === selectedKey)) {
      setSelectedKey(defaultKey);
    }
  }, [defaultKey, allVersions, selectedKey]);

  if (!rows.length) {
    return (
      <Section title="Creator app versions" accent={T.chart.violet}
        summary="No app version data — creators must have made/received at least one call for their version to be recorded in call_logs." />
    );
  }

  const selected = allVersions.find(v => `${v.platform}::${v.version}` === selectedKey) || allVersions[0];

  const totalCount  = rows.length;
  const androidVersions = allVersions.filter(v => v.platform === "android");
  const iosVersions     = allVersions.filter(v => v.platform === "ios");
  const otherVersions   = allVersions.filter(v => v.platform !== "android" && v.platform !== "ios");
  const androidLatest   = androidVersions.find(v => v.isLatest)?.version;
  const iosLatest       = iosVersions.find(v => v.isLatest)?.version;

  const summary = [
    `${totalCount} creators (all-time)`,
    androidLatest ? `Android latest ${androidLatest}` : null,
    iosLatest     ? `iOS latest ${iosLatest}`         : null,
    "sourced from call_logs",
  ].filter(Boolean).join(" · ");

  const fmtDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" });
  };

  const platformLabel = (p) => p === "android" ? "Android" : p === "ios" ? "iOS" : "Unknown";
  const platformIcon  = (p) => p === "android" ? "🤖" : p === "ios" ? "🍎" : "❔";

  // Each version chip — clickable, highlights when selected.
  const VersionChip = ({ v }) => {
    const key = `${v.platform}::${v.version}`;
    const isSel = key === selectedKey;
    return (
      <button type="button" onClick={() => setSelectedKey(key)}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 10, width: "100%",
          padding: "10px 12px", borderRadius: 10,
          border: `1px solid ${isSel ? T.chart.violet : T.border.subtle}`,
          background: isSel ? T.chart.violet : T.surface.card,
          color: isSel ? "#FFFFFF" : T.text.primary,
          cursor: "pointer", textAlign: "left", fontFamily: "inherit",
          transition: "all 0.15s",
        }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <span style={{
            fontFamily: T.font.mono, fontSize: 13, fontWeight: 800,
            color: isSel ? "#FFFFFF" : T.text.primary,
          }}>
            v{v.version}
          </span>
          {v.isLatest && (
            <span style={{
              fontSize: 9, fontWeight: 800, letterSpacing: "0.06em",
              padding: "2px 6px", borderRadius: 999,
              background: isSel ? "rgba(255,255,255,0.25)" : T.status.good,
              color: "#FFFFFF",
            }}>LATEST</span>
          )}
        </div>
        <span style={{
          fontSize: 11, fontWeight: 700,
          color: isSel ? "rgba(255,255,255,0.85)" : T.text.muted,
          fontFamily: T.font.mono,
        }}>{v.count}</span>
      </button>
    );
  };

  // Each platform's version list (a labelled section in the picker pane)
  const VersionGroup = ({ label, icon, versions }) => {
    if (versions.length === 0) return null;
    return (
      <div style={{ marginBottom: 14 }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 6,
          fontSize: 10, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase",
          color: T.text.muted, padding: "4px 4px 8px",
        }}>
          <span style={{ fontSize: 13 }}>{icon}</span>
          <span>{label}</span>
          <span style={{ marginLeft: "auto", color: T.text.muted, fontWeight: 700 }}>
            {versions.reduce((s, v) => s + v.count, 0)}
          </span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {versions.map(v => <VersionChip key={`${v.platform}::${v.version}`} v={v} />)}
        </div>
      </div>
    );
  };

  return (
    <Section title="Creator app versions" accent={T.chart.violet} summary={summary}>
      {/* Two-pane layout — picker on left, creator list on right.
          1fr collapses to single column under 720px via media-friendly flex. */}
      <div style={{
        gridColumn: "1 / -1",
        display: "grid",
        gridTemplateColumns: "minmax(220px, 280px) 1fr",
        gap: 16,
        alignItems: "start",
      }}>
        {/* LEFT — version picker. No maxHeight so every version is visible
            without scrolling. The right pane scrolls instead (long creator
            lists), but the picker itself is the navigation surface and
            should always be fully visible. */}
        <div style={{
          background: T.surface.canvas,
          borderRadius: 12,
          padding: 12,
          border: `1px solid ${T.border.subtle}`,
        }}>
          <VersionGroup label="Android" icon="🤖" versions={androidVersions} />
          <VersionGroup label="iOS"     icon="🍎" versions={iosVersions} />
          {otherVersions.length > 0 && <VersionGroup label="Other" icon="❔" versions={otherVersions} />}
        </div>

        {/* RIGHT — creators on the selected version */}
        <div style={{
          background: T.surface.canvas,
          borderRadius: 12,
          border: `1px solid ${T.border.subtle}`,
          overflow: "hidden",
        }}>
          {/* Header for the selected version */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            gap: 10, flexWrap: "wrap",
            padding: "12px 16px",
            background: selected.isLatest ? T.status.goodBg : "rgba(31,42,63,0.03)",
            borderBottom: `1px solid ${T.border.subtle}`,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
              <span style={{ fontSize: 16 }}>{platformIcon(selected.platform)}</span>
              <span style={{ fontSize: 13, fontWeight: 800, color: T.text.primary }}>
                {platformLabel(selected.platform)}
              </span>
              <span style={{
                fontFamily: T.font.mono, fontSize: 14, fontWeight: 800,
                color: selected.isLatest ? T.status.good : T.text.primary,
                padding: "2px 8px", borderRadius: 6,
                background: selected.isLatest ? "rgba(76,175,80,0.18)" : "rgba(31,42,63,0.06)",
              }}>
                v{selected.version}
              </span>
              {selected.isLatest ? (
                <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.08em",
                  padding: "3px 8px", borderRadius: 999,
                  background: T.status.good, color: "#FFFFFF" }}>LATEST</span>
              ) : (
                <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.08em",
                  padding: "3px 8px", borderRadius: 999,
                  background: T.status.warn, color: "#FFFFFF" }}>OUTDATED</span>
              )}
            </div>
            <span style={{ fontSize: 12, fontWeight: 700, color: T.text.secondary }}>
              {selected.count} {selected.count === 1 ? "creator" : "creators"}
            </span>
          </div>

          {/* Creator list — sorted by last seen desc */}
          <div style={{ maxHeight: 480, overflowY: "auto" }}>
            {selected.creators
              .slice()
              .sort((a, b) => new Date(b.lastSeen) - new Date(a.lastSeen))
              .map((c, i) => (
                <div key={c.creatorId}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "10px 16px", gap: 12,
                    borderTop: i === 0 ? "none" : `1px solid ${T.border.subtle}`,
                    background: i % 2 === 0 ? T.surface.card : "transparent",
                  }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: T.text.primary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {c.name}
                    </div>
                    {c.username && (
                      <div style={{ fontSize: 10, color: T.text.muted, marginTop: 1 }}>
                        @{c.username}
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2, fontSize: 10, color: T.text.muted, flexShrink: 0 }}>
                    {c.osVersion && <div>{c.osVersion}</div>}
                    {c.deviceModel && <div style={{ maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.deviceModel}</div>}
                    <div style={{ fontFamily: T.font.mono }}>{fmtDate(c.lastSeen)}</div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </Section>
  );
}

export function CreatorsPage({ range, rangeId }) {
  const { data, loading, error, reload } = useDashboardPage("creators", range, rangeId);
  // Per-creator earnings sections — respects the date pill (Till-date = all-time).
  const { data: earnings } = useDashboardPage("creators/earnings", range, rangeId);
  if (loading) return <LoadingShell tiles={5} />;
  if (!data) return <EmptyError title="Couldn't load Creators" error={error} onRetry={reload} />;

  const h = data.hero || {};
  const isTillDate = data.range?.isTillDate;
  const word = rangeWordFor(rangeId);
  // Counts that are anchored to end-of-window (created_at <= toIst) read as
  // "lifetime" only on Till-date; otherwise they read as "as of end of <pill>".
  const asOfWord = isTillDate ? "lifetime" : `as of end of ${word}`;

  // Hero — 5 tiles. KYC-complete tile removed per CEO 2026-04-28 (not actionable yet).
  // Defensive `?.value` access — backend may return partial hero on degraded query.
  const heroTiles = [
    { label: "Total creators",        icon: "T", variant: "muted-violet",  value: formatNum(h.totalCreators?.value),  subValue: h.newToday?.value ? `+${formatNum(h.newToday.value)} today` : asOfWord },
    { label: `Took a call · ${word}`, icon: "A", variant: "muted-lime",    value: formatNum(h.activeWindow?.value),    subValue: `completed at least 1 call ${word === 'lifetime' ? 'all-time' : 'in ' + word}` },
    { label: "Accepting calls",       icon: "F", variant: "muted-orange",  value: formatNum(h.flaggedAvailable?.value), subValue: `available to receive calls · ${asOfWord}` },
    { label: "Off for calling",       icon: "O", variant: "muted-magenta", value: formatNum(h.offForCalling?.value),    subValue: `toggle off or account inactive · ${asOfWord}` },
    { label: "Verified by Fancall",   icon: "V", variant: "muted-lime",    value: formatNum(h.verifiedByFancall?.value), subValue: `profile approved by Fancall · ${asOfWord}` },
    { label: "At-risk creators",      icon: "X", variant: "muted-magenta", value: formatNum(h.atRisk?.value),          subValue: `idle 30+ days, or calls halved vs last month · ${asOfWord}` },
  ];

  // Top creators leaderboard rows — TOTAL-LEVEL reconciliation.
  //   line 1 = lifetime earned BY SOURCE (calls / brand / referral)
  //   line 2 = totals: withdrawn + in-wallet (+ pending) — earned ≈ withdrawn + wallet
  //   line 3 = referrals (fans + creators they brought in) — lifetime
  // Withdrawn and wallet are shown as TOTALS, not per-source: a single
  // withdrawal pools call + referral money, so the DB can't split it by source.
  // Each amount renders as <span title="full rupee value"> so hovering shows
  // the exact rupee figure (no rounded-Cr/L confusion).
  const fullINR = (n) => "Rs " + Math.round(n || 0).toLocaleString("en-IN");
  const join = (parts, sep = " · ") =>
    parts.flatMap((p, i) => i === 0 ? [p] : [<span key={`s${i}`}>{sep}</span>, p]);

  const top = (data.topCreators || []).map((r) => {
    // Earned by source (lifetime, now correctly sourced): Calls = NET
    // youtuber-earning, Brand = brand + affiliate campaigns, Referral = true
    // referral bonuses. Total earned = the sum.
    const callE  = r.callEarnedLifetime || 0;
    const brandE = r.brandEarnedLifetime || 0;
    const refE   = r.referralEarnedLifetime || 0;
    const lifetimeGross = callE + brandE + refE;

    // Withdrawn + wallet are TOTALS (can't be split per source). The honest
    // reconciliation: earned ≈ withdrawn + in-wallet (+ pending + fees/tax).
    const totalWithdrawn = r.withdrawnLifetime || 0;
    const totalWallet    = (r.walletBalance || 0) + (r.referralBalance || 0) + (r.campaignBalance || 0);
    const pending        = r.pendingPayout || 0;

    const earnedTip =
      `Lifetime earned: ${fullINR(lifetimeGross)}\n` +
      `  Calls (net):  ${fullINR(callE)}\n` +
      `  Brand:        ${fullINR(brandE)}\n` +
      `  Referral:     ${fullINR(refE)}`;
    const reconcileTip =
      `Reconciliation (earned ≈ withdrawn + in wallet):\n` +
      `  Earned:    ${fullINR(lifetimeGross)}\n` +
      `  Withdrawn: ${fullINR(totalWithdrawn)}\n` +
      `  In wallet: ${fullINR(totalWallet)}` +
      (pending > 0 ? `\n  (plus ${fullINR(pending)} queued for payout)` : '');

    const earnedLine = (
      <span title={earnedTip}>
        <strong>Earned</strong> — calls {formatINR(callE)} · brand {formatINR(brandE)} · referral {formatINR(refE)}
      </span>
    );
    const moneyLine = (
      <span title={reconcileTip}>
        <strong>Withdrawn</strong> {formatINR(totalWithdrawn)} · <strong>in wallet</strong> {formatINR(totalWallet)}
        {pending > 0 && <> · pending {formatINR(pending)}</>}
      </span>
    );

    // Referrals brought in (lifetime, organic)
    const refParts = [];
    if (r.fansReferred > 0)
      refParts.push(<span key="f" title={`Fans referred (lifetime, organic — synthetic inviters excluded): ${formatNum(r.fansReferred)}`}>{formatNum(r.fansReferred)} {r.fansReferred === 1 ? 'fan' : 'fans'} referred</span>);
    if (r.creatorsReferred > 0)
      refParts.push(<span key="c" title={`Creators referred (lifetime, organic — synthetic inviters excluded): ${formatNum(r.creatorsReferred)}`}>{formatNum(r.creatorsReferred)} {r.creatorsReferred === 1 ? 'creator' : 'creators'} referred</span>);

    return {
      name: r.name,
      value: lifetimeGross,
      displayValue: formatINR(lifetimeGross),
      displayTitle: `${earnedTip}\n\n${reconcileTip}`,
      subtitle:  earnedLine,
      subtitle2: moneyLine,
      subtitle3: refParts.length > 0 ? <span>{join(refParts)}</span> : null,
      subtitle4: null,
    };
  });

  // Supply signals — useful supplementary tiles
  const supply = data.supplySignals || {};
  const supplyTiles = [
    { label: "Has availability set", icon: "S", variant: "muted-orange", value: formatNum(supply.hasAvailabilitySet), subValue: "configured weekly schedule" },
    { label: "Instagram linked",     icon: "I", variant: "muted-magenta", value: formatNum(supply.instaLinked),       subValue: supply.instaTokenExpired > 0 ? `${formatNum(supply.instaTokenExpired)} tokens expired` : "all tokens valid" },
    { label: "YouTube linked",       icon: "Y", variant: "muted-violet",  value: formatNum(supply.youtubeLinked),     subValue: "channel verified" },
  ];

  const trend = data.trend?.cumulativeCreatorsLast12Months || [];
  const latest = trend[trend.length - 1]?.value || 0;
  const prev   = trend[trend.length - 2]?.value || 0;
  const delta  = prev > 0 ? ((latest - prev) / prev) * 100 : 0;

  return (
    <>
      <div style={{ marginBottom: 16, padding: "0 4px" }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: T.text.primary }}>Creators</div>
        <div style={{ fontSize: 12, color: T.text.muted, marginTop: 4 }}>Supply side · live from production · {word}</div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(220px, 100%), 1fr))", gap: 14, marginBottom: 12 }}>
        {heroTiles.map(t => <Tile key={t.label} {...t} />)}
      </div>

      {trend.length > 0 && (() => {
        const gran = data.range?.granularity || 'month';
        const granLabel = glab(gran);
        const granWord  = gword(gran);
        const titleSuffix = isTillDate ? 'last 12 months' : word;
        return (
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10, padding: "0 4px" }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.text.primary }}>Total creators — {titleSuffix}</div>
                <div style={{ fontSize: 11, color: T.text.muted, marginTop: 2 }}>live · cumulative count by {granWord.replace(/ly$/, '')}-end</div>
              </div>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.text.muted, padding: "4px 10px", background: T.surface.card, border: "1px solid " + T.border.subtle, borderRadius: 999 }}>{granLabel}</span>
            </div>
            <AreaChart
              data={trend}
              format={formatNum}
              summaryLabel={trend[trend.length - 1]?.label || "Latest"}
              summaryValue={formatNum(latest)}
              summaryDelta={(delta >= 0 ? "+" : "") + delta.toFixed(1) + "% vs prev"}
            />
          </div>
        );
      })()}

      {top.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <Leaderboard
            title="Top earning creators"
            subtitle="Lifetime gross by source · earned → withdrawn → remaining (math balances). Hover any amount for the exact rupee value."
            rows={top}
            rightLabel="Lifetime gross"
          />
        </div>
      )}

      <Section title="Supply signals" accent="#7C4DFF" count={supplyTiles.length} summary={`${asOfWord.charAt(0).toUpperCase() + asOfWord.slice(1)} · creator profile completeness + social linkage`}>
        {supplyTiles.map(t => <Tile key={t.label} {...t} />)}
      </Section>

      <CreatorEarningsTables data={earnings} />
      <AppVersionsTable rows={data.appVersions || []} />

      <PageGuide groups={CREATORS_GUIDE} />
    </>
  );
}

// Definitions for every Creators-page card. "Completed creator" = is_active OR
// is_verify. Call earnings come from transaction_history (youtuber-earning), NOT
// the earnings table (which over-counts and has NaN cells).
const CREATORS_GUIDE = [
  {
    section: "Overview",
    rows: [
      { card: "Total creators", means: "Every creator account ever created, as of the end of the selected dates.", rule: "users role=creator, deleted_at IS NULL, created ≤ range end." },
      { card: "New today", means: "Creators who signed up on the literal current day (not affected by the date pill).", rule: "users role=creator created today (IST)." },
      { card: "Took a call · window", means: "Creators with at least one COMPLETED call in the period.", rule: "distinct celebrity_id in celebrity_slot_booking status='completed', in window." },
      { card: "Accepting calls", means: "Creators available to receive calls right now (availability toggle on + active account).", rule: "users role=creator, is_available_for_booking=true AND is_active=true (as of range end)." },
      { card: "Off for calling", means: "Creators NOT available to receive calls (availability toggle off, or account inactive).", rule: "total creators − accepting calls." },
      { card: "Verified by Fancall", means: "Creators approved by Fancall (internal approval flag, not legal KYC).", rule: "users role=creator, is_verify=true." },
      { card: "At-risk creators", means: "Active creators who've gone quiet — no completed call in 30+ days, or their calls halved month-on-month.", rule: "users is_active=true joined with their last completed call in celebrity_slot_booking." },
    ],
  },
  {
    section: "Growth & earnings",
    rows: [
      { card: "Total creators (trend)", means: "Running total of creators ever registered, by the end of each bucket.", rule: "users role=creator cumulative count (auto granularity)." },
      { card: "Top earning creators", means: "Top 10 by earnings, each with earned (window), withdrawn (lifetime) and referrals.", rule: "Earned = transaction_history type='youtuber-earning' (net call income) + brand/affiliate campaign earnings + referral rewards, in window, NaN-guarded. Withdrawn = completed withdraw_requests (lifetime); in-wallet = wallets pots." },
    ],
  },
  {
    section: "Supply signals",
    rows: [
      { card: "Has availability set", means: "Creators who configured at least one available day.", rule: "celebrity_available_days with any weekday set." },
      { card: "Instagram / YouTube linked", means: "Creators who connected their Instagram or YouTube.", rule: "a row in celebrity_insta_account / channels." },
      { card: "App versions", means: "Which app version each creator is on (to spot outdated apps).", rule: "call_logs app_version (all-time) merged with Mixpanel last-30d activity." },
    ],
  },
  {
    section: "Earnings breakdown (bottom tables)",
    rows: [
      { card: "Earn through calling", means: "Creators with 1+ completed call, ranked by call earnings.", rule: "transaction_history type='youtuber-earning' (NaN rows dropped → lower bound); creators with ≥1 completed celebrity_slot_booking call. Lifetime." },
      { card: "Earn through brand deals", means: "Creators with 1+ brand deal, ranked by brand earnings.", rule: "SUM(campaign_enrollments.earnings) (brand + affiliate), NaN-guarded; creators with ≥1 enrollment. Lifetime." },
      { card: "Earn through referral", means: "Creators who referred 1+ fan or creator, with rule-based rewards.", rule: "₹50 × fans referred (paid) + ₹750 × creators referred (paid) + ₹250 if they joined via a creator link. From user_referral. Lifetime." },
      { card: "Withdrawals — calling", means: "Completed payouts creators took from their call-earnings pot (amount + count).", rule: "withdraw_requests, status='completed', NaN-guarded, per creator. Lifetime." },
      { card: "Withdrawals — brand", means: "Completed payouts creators took from their brand-earnings pot (amount + count).", rule: "brand_withdraw_requests, status='completed', NaN-guarded, per creator. Lifetime." },
    ],
  },
];
